/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo } from 'react';
import { AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react';
import { checkAllPairs, dictionaryInfo } from '@/lib/localInteractions';

const SEVERITY_STYLE = {
  critical: {
    color: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800',
    label: 'CRITICO',
    Icon: ShieldAlert,
  },
  high: {
    color: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800',
    label: 'ALTO',
    Icon: AlertTriangle,
  },
  medium: {
    color: 'text-orange-700 dark:text-orange-300',
    bg: 'bg-orange-50 dark:bg-orange-950/40 border-orange-300 dark:border-orange-800',
    label: 'MEDIO',
    Icon: AlertTriangle,
  },
  low: {
    color: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800',
    label: 'BASSO',
    Icon: AlertTriangle,
  },
};

/**
 * LocalInteractionAlerts — client-side visual block that flags conflicts
 * detected by the offline drug interaction dictionary.
 *
 * Props:
 *   drugs   — array of drug name strings currently in the prescription form
 *   activeTherapy — array of drug name strings already in the patient's card
 *
 * The component is silent (returns null) when no interactions are found.
 */
export const LocalInteractionAlerts = ({ drugs = [], activeTherapy = [] }) => {
  const findings = useMemo(() => {
    const combined = [...drugs, ...activeTherapy].filter(Boolean);
    if (combined.length < 2) return [];
    return checkAllPairs(combined).findings;
  }, [drugs, activeTherapy]);

  if (findings.length === 0) {
    // Silent success — render a compact “all-clear” pill only when at least
    // two drugs are being compared so the doctor knows the check ran.
    if ((drugs || []).filter(Boolean).length + (activeTherapy || []).filter(Boolean).length >= 2) {
      return (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300" data-testid="local-interactions-clear">
          <ShieldCheck className="h-4 w-4" />
          Nessuna interazione rilevata dal dizionario locale ({dictionaryInfo.rulesCount} regole)
        </div>
      );
    }
    return null;
  }

  return (
    <div className="space-y-2" data-testid="local-interaction-alerts">
      {findings.map((f, i) => {
        const style = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.medium;
        const Icon = style.Icon;
        return (
          <div
            key={`${f.drugA}-${f.drugB}-${i}`}
            className={`p-3 rounded-lg border-2 ${style.bg}`}
            data-testid={`local-interaction-${f.severity}`}
          >
            <div className={`flex items-center gap-2 font-bold text-sm ${style.color}`}>
              <Icon className="h-4 w-4" />
              Interazione {style.label} — {f.drugA} × {f.drugB}
            </div>
            <p className={`text-xs mt-1 ${style.color}`}>{f.reason}</p>
          </div>
        );
      })}
      <div className="text-[10px] text-muted-foreground text-right">
        Fonte: dizionario locale AIFA/BNF/Micromedex v{dictionaryInfo.version} —
        controllo eseguito offline, non sostituisce il giudizio clinico.
      </div>
    </div>
  );
};

export default LocalInteractionAlerts;
